import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button } from '@mui/material';
import GeneralUpload from 'src/components/general/GeneralUpload';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { useEffect, useState } from 'react';
import { generateSelectMenuItems } from 'src/utils';
import { GeneralSelectMenu } from 'src/components/general';
import { networkCreateWheelItem, networkUpdateWheelItem, useFetchWheelItemTypeList } from 'src/network/wheel-item';
import useAppInfoStore from 'src/store/appInfo';
import { useQueryClient } from '@tanstack/react-query';
import useFormsModalStore from 'src/store/forms';
import { useParams } from 'react-router-dom';
import { SkeletonForm } from 'src/components/skeleton';

const WheelItemForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const { wheelId } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { appInfo } = useAppInfoStore();

  const [wheelItemTypeList, setWheelItemTypeList] = useState([]);
  const [selectedWheelItemType, setSelectedWheelItemType] = useState('');

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const wheelItemDetails = modal?.data;
  const defaultValues = getDefaultValues(wheelItemDetails);
  const isEditMode = wheelItemDetails;

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // ---------------------------------------------------------------
  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      WheelItemWheelID: +wheelId,
      WheelItemWheelItemTypeID: selectedWheelItemType,
      WheelItemQuantity: +values?.WheelItemQuantity,
      WheelItemProbability: +values?.WheelItemProbability,
      WheelItemOrder: +values?.WheelItemOrder,
      WheelItemReward: +values?.WheelItemReward,
      WheelItemTransactionAmountTypeID: selectedAmountType,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateWheelItem(wheelItemDetails?.WheelItemID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries(['wheel-items-list']);
          }
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateWheelItem(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries(['wheel-items-list']);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { isLoading, data } = useFetchWheelItemTypeList();

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');
    setAmountTypes(amountTypeList);
    setSelectedAmountType(amountTypeList[0]?.id);
    setSelectedAmountType(wheelItemDetails?.WheelItemTransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  useEffect(() => {
    if (data) {
      const wheelItemTypes = generateSelectMenuItems(data?.data?.items, 'WheelItemTypeLabel', 'WheelItemTypeID');
      setWheelItemTypeList(wheelItemTypes);
      setSelectedWheelItemType(wheelItemDetails?.WheelItemTypeID || wheelItemTypes?.[0]?.id);
    }
  }, [data]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={12} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField name="WheelItemLabel" type="text" label="عنوان" />

                  <GeneralSelectMenu
                    label="ارزش خانه"
                    name="WheelItemWheelItemTypeID"
                    options={wheelItemTypeList}
                    value={selectedWheelItemType}
                    onChangeValue={setSelectedWheelItemType}
                  />

                  <GeneralUpload
                    label="تصویر خانه"
                    id="Wheel_item_image"
                    name="WheelItemImage"
                    afterChangeAction={(res) => {
                      setValue('WheelItemImage', res);
                      trigger('WheelItemImage');
                    }}
                  />

                  <GeneralUpload
                    label="تصویر اعلان"
                    id="Wheel_item_popup_image"
                    name="WheelItemPopupImage"
                    afterChangeAction={(res) => {
                      setValue('WheelItemPopupImage', res);
                      trigger('WheelItemPopupImage');
                    }}
                  />

                  <RHFTextField name="WheelItemQuantity" type="text" label="تعداد" />
                  <RHFTextField name="WheelItemProbability" type="text" label="وزن (درصد)" />

                  <GeneralSelectMenu
                    label="نوع پاداش"
                    name="WheelItemTransactionAmountTypeID"
                    options={amountTypes}
                    value={selectedAmountType}
                    onChangeValue={setSelectedAmountType}
                  />

                  <RHFTextField name="WheelItemReward" type="text" label="تعداد پاداش" />

                  <RHFTextField name="WheelItemOrder" type="text" label="ترتیب" />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)' },
                    mt: 3,
                  }}
                >
                  <RHFTextField name="WheelItemDescription" multiline rows={6} label="متن نمایش پاپ آپ" />
                </Box>

                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default WheelItemForm;
